export type PlanTier = "free" | "pro";

export type PlanLimits = {
  maxItems: number | null;
  maxPhotosPerItem: number | null;
  allowsReceipts: boolean;
  allowsCustomSpecs: boolean;
  allowsReminders: boolean;
};

export const PRO_PRICE_TEXT = "US$2.99";

/** ISO code for the price above. Stated in the UI so the amount is unambiguous. */
export const PRO_PRICE_CURRENCY = "USD";

/**
 * Length of the introductory free trial.
 *
 * Not configured anywhere yet — the RevenueCat Test Store cannot hold a trial,
 * and a real one is an introductory offer created in App Store Connect and Play
 * Console. Until then this is the length the copy promises, not one anybody
 * receives. See REVENUECAT_SETUP.md.
 */
export const PRO_TRIAL_DAYS = 14;

/**
 * Spec keys free accounts may store on an item. Anything else is a custom
 * field and requires GearSync Pro.
 */
export const BUILT_IN_SPEC_KEYS = [
  "chuck",
  "voltage",
  "wattage",
  "weight",
  "size",
  "capacity",
  "rpm",
  "torque",
  "battery",
  "connectivity",
  "aperture",
  "resolution",
  "lens",
  "ram",
  "storage",
  "screen",
  "fuel",
  "displacement",
  "thread",
  "length",
  "width",
  "height",
] as const;

export type BuiltInSpecKey = (typeof BUILT_IN_SPEC_KEYS)[number];

export const BUILT_IN_SPEC_LABELS: Record<BuiltInSpecKey, string> = {
  chuck: "Chuck",
  voltage: "Voltage",
  wattage: "Wattage",
  weight: "Weight",
  size: "Size",
  capacity: "Capacity",
  rpm: "RPM",
  torque: "Torque",
  battery: "Battery",
  connectivity: "Connectivity",
  aperture: "Aperture",
  resolution: "Resolution",
  lens: "Lens",
  ram: "RAM",
  storage: "Storage",
  screen: "Screen",
  fuel: "Fuel",
  displacement: "Displacement",
  thread: "Thread",
  length: "Length",
  width: "Width",
  height: "Height",
};

/**
 * Mirrors the `plan_limits` table. The database is the real gate; this copy
 * exists so an offline client can disable the button before the write fails.
 */
export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    maxItems: 10,
    maxPhotosPerItem: 1,
    allowsReceipts: false,
    allowsCustomSpecs: false,
    allowsReminders: false,
  },
  pro: {
    maxItems: null,
    maxPhotosPerItem: null,
    allowsReceipts: true,
    allowsCustomSpecs: true,
    allowsReminders: true,
  },
};

export type Allowance = {
  allowed: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  message: string | null;
};

function allowance(
  used: number,
  limit: number | null,
  noun: string,
): Allowance {
  if (limit === null) {
    return { allowed: true, limit: null, used, remaining: null, message: null };
  }
  const remaining = Math.max(limit - used, 0);
  return {
    allowed: remaining > 0,
    limit,
    used,
    remaining,
    message:
      remaining > 0
        ? null
        : `Free accounts include ${limit} ${limit === 1 ? noun : `${noun}s`}. Upgrade to Pro for unlimited.`,
  };
}

export function itemAllowance(tier: PlanTier, used: number): Allowance {
  return allowance(used, PLAN_LIMITS[tier].maxItems, "item");
}

export function photoAllowance(tier: PlanTier, used: number): Allowance {
  return allowance(used, PLAN_LIMITS[tier].maxPhotosPerItem, "photo");
}

export function allowsReceipts(tier: PlanTier) {
  return PLAN_LIMITS[tier].allowsReceipts;
}

export function allowsCustomSpecs(tier: PlanTier) {
  return PLAN_LIMITS[tier].allowsCustomSpecs;
}

export function allowsReminders(tier: PlanTier) {
  return PLAN_LIMITS[tier].allowsReminders;
}

export function isBuiltInSpecKey(key: string): key is BuiltInSpecKey {
  return (BUILT_IN_SPEC_KEYS as readonly string[]).includes(key);
}

export function customSpecKeys(
  specs: Record<string, unknown> | null | undefined,
) {
  return Object.keys(specs ?? {}).filter((key) => !isBuiltInSpecKey(key));
}

/**
 * Postgres raises plan-limit violations as P0001 with a human-readable
 * message, so surfacing it directly beats a generic failure toast.
 */
export function planLimitMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as { code?: string; message?: string };
  if (candidate.code !== "P0001" || !candidate.message) return null;
  return candidate.message.includes("GearSync Pro") ? candidate.message : null;
}
